import styled from 'styled-components';
import type { Theme } from '@/styles/theme';
import { truncateLine } from '@/styles/mixins';

export { EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription, TextCaption, DesktopOnly, MobileOnly, MobileCardList, MobileCard, MobileCardHeader, MobileCardBody, MobileCardActions } from '@/styles/shared.styled';

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;

  .form-select-sm {
    width: auto;
  }
`;

export const LabelFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  flex-wrap: wrap;
`;

export const LabelFilterCaption = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const FilterPill = styled.span`
  cursor: pointer;
  font-size: 0.75rem;

  .badge {
    cursor: pointer;
  }
`;

export const SortableHeaderCell = styled.th`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
`;

export const CheckboxCell = styled.th`
  width: 2rem;
`;

export const FileIconCell = styled.th`
  width: 2.5rem;
`;

export const ActionsCell = styled.th`
  width: 8rem;
`;

const fileTypeColors = (theme: Theme, type: string): { bg: string; color: string } => {
  switch (type) {
    case 'pdf': return { bg: 'rgba(220, 38, 38, 0.1)', color: theme.colors.danger };
    case 'docx': return { bg: 'rgba(37, 99, 235, 0.1)', color: theme.colors.info };
    case 'rtf':
    default: return { bg: 'rgba(71, 85, 105, 0.1)', color: theme.colors.secondary };
  }
};

export const FileIcon = styled.div<{ $type: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radii.lg};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  background-color: ${({ $type, theme }) => fileTypeColors(theme, $type).bg};
  color: ${({ $type, theme }) => fileTypeColors(theme, $type).color};
`;

export const DocumentName = styled.div`
  ${truncateLine}
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const CellText = styled.td`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  white-space: nowrap;
`;

export const SummaryText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 0.125rem;
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const Summarizing = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
  margin-top: 0.125rem;
`;

export const LabelBadge = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  padding: 0.1rem 0.4rem;
  border-radius: ${({ theme }) => theme.radii.full};
  background-color: ${({ theme }) => theme.colors.primary50};
  color: ${({ theme }) => theme.colors.primary400};
  margin-right: 0.25rem;
  margin-top: 0.25rem;
  white-space: nowrap;
`;

export const LabelsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
`;

export const NameCell = styled.td`
  max-width: 30rem;
`;

export const NameContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ImportSourceIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 0.25rem;
  opacity: 0.6;
`;
